import React, { useEffect, useState } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import WebOutlinedIcon from "@mui/icons-material/WebOutlined";
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    TextField,
} from "@mui/material";

import { routes } from "Constant/routes";
import { useDebounce } from "hooks/useDebounce";
import { useNavigate, useSearchParams } from "react-router-dom";

import EmptyState from "components/atoms/EmptyState";
import PageContainer from "components/atoms/PageContainer";
import { ConfirmationModal } from "components/organisms/ConfirmationModal";
import GenericTable from "components/organisms/GenericTable";

import { createLandingPagesListColumns } from "./LandingPages.helpers";
import { LandingPagesProps } from "./LandingPages.types";
import URLsModal, { LandingPageUrlsModal } from "./URLsModal";

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const MOCK_LANDING_PAGE = {
    name: "Summer Sale Landing Page",
    page_url: "https://example.com/lp/summer-sale",
};
const __DEV__ = process.env.NODE_ENV === "development";

const LandingPages = ({
    landingPages,
    language,
    isCloneLandingPageLoading,
    deletingLandingPageId,
    dispatchGetLandingPages,
    dispatchDeleteLandingPage,
    dispatchSetLandingPageDetails,
    dispatchClearState,
}: LandingPagesProps) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const trackerId = searchParams.get("id");

    // Pagination and search state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [searchInput, setSearchInput] = useState("");
    const debouncedSearchTerm = useDebounce(searchInput, 1000);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
    const [previewUrlsModalOpen, setPreviewUrlsModalOpen] = useState(false);

    const handleSearchInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setSearchInput(event.target.value);
    };

    const handleClearSearch = () => {
        setSearchInput("");
    };

    // Reset to the first page whenever the (debounced) search term or page
    // size changes, rather than on every keystroke.
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, pageSize]);

    useEffect(() => {
        dispatchGetLandingPages({
            page: currentPage,
            page_size: pageSize,
            search: debouncedSearchTerm,
        });
        return () => dispatchClearState({ clearDetails: false });
    }, [
        dispatchGetLandingPages,
        dispatchClearState,
        currentPage,
        pageSize,
        debouncedSearchTerm,
    ]);

    return (
        <PageContainer
            title={"Landing Pages"}
            subtitle={
                "On this page, you can view landing pages, and create a new one by using the button."
            }
            variant="list"
            loading={landingPages?.isLoadingList}
            additionalButtons={
                <Box sx={{ display: "flex", gap: 1 }}>
                    {false && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setPreviewUrlsModalOpen(true)}
                        >
                            Preview URLs Modal
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() =>
                            navigate(
                                `${routes.create_landing_page}?id=${trackerId}`,
                            )
                        }
                    >
                        Create Landing Page
                    </Button>
                </Box>
            }
        >
            <ConfirmationModal
                open={confirmOpen}
                onClose={() => {
                    setConfirmOpen(false);
                    setPendingDeleteId(null);
                }}
                onConfirm={() => {
                    if (pendingDeleteId !== null)
                        dispatchDeleteLandingPage(pendingDeleteId);
                    setConfirmOpen(false);
                    setPendingDeleteId(null);
                }}
                title={"Are you sure?"}
                message="This action cannot be undone."
                confirmColor="error"
            />

            <LandingPageUrlsModal
                open={previewUrlsModalOpen}
                onClose={() => setPreviewUrlsModalOpen(false)}
                landingPageName={MOCK_LANDING_PAGE.name}
                pageUrl={MOCK_LANDING_PAGE.page_url}
            />

            {/* Search Controls */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, sm: 2 },
                    mb: 2,
                    mt: 1,
                    flexDirection: { xs: "column", sm: "row" },
                }}
            >
                <TextField
                    placeholder="Search landing pages..."
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: searchInput && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={handleClearSearch}
                                    edge="end"
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        minWidth: { xs: "100%", sm: 300 },
                        width: { xs: "100%", sm: "auto" },
                    }}
                />
            </Box>
            <Box
                position="relative"
                sx={{ maxWidth: "100%", minWidth: 0, width: "100%" }}
            >
                <URLsModal />
                <GenericTable
                    columns={createLandingPagesListColumns({
                        navigate,
                        dispatchSetLandingPageDetails,
                        deletingLandingPageId,
                        onRequestDelete: (id: number) => {
                            setPendingDeleteId(id);
                            setConfirmOpen(true);
                        },
                    })}
                    rows={landingPages?.data || []}
                    getRowKey={(row: any) => row.id}
                    emptyState={
                        debouncedSearchTerm ? (
                            <EmptyState
                                icon={<SearchIcon />}
                                title="No landing pages found"
                                description="No landing pages match your search. Try a different keyword."
                            />
                        ) : (
                            <EmptyState
                                icon={<WebOutlinedIcon />}
                                title="No landing pages yet"
                                description="Create your first landing page to start capturing leads for this tracker."
                            />
                        )
                    }
                    loading={landingPages?.isLoadingList}
                    currentPage={
                        landingPages?.pagination?.current_page ?? currentPage
                    }
                    totalPages={landingPages?.pagination?.last_page ?? 1}
                    totalItems={landingPages?.pagination?.total ?? 0}
                    onPageChange={setCurrentPage}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    columnToggle
                    tableName="landing_pages"
                />
            </Box>
        </PageContainer>
    );
};

export default LandingPages;
